import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './services/supabase.js';
import { Layout } from './components/Layout.js';
import { Onboarding } from './components/Onboarding.js';
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { PricingPage } from './pages/PricingPage.js';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage.js';
import { CheckoutCancelPage } from './pages/CheckoutCancelPage.js';
import { TryPage } from './pages/TryPage.js';
import { SendersPage } from './pages/SendersPage.js';
import { TeamPage } from './pages/TeamPage.js';
import { JoinTeamPage } from './pages/JoinTeamPage.js';
import type { Session } from '@supabase/supabase-js';

const ONBOARDING_KEY = 'aas_onboarding_done';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session && !localStorage.getItem(ONBOARDING_KEY)) {
        setShowOnboarding(true);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  function handleOnboardingComplete() {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShowOnboarding(false);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-ink-muted text-sm">Loading...</p>
      </div>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/join/:inviteToken" element={<JoinTeamPage token={null} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const token = session.access_token;
  const user = { email: session.user.email ?? '' };

  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} userEmail={user.email} />;
  }

  return (
    <Layout user={user}>
      <Routes>
        <Route path="/dashboard" element={<DashboardPage token={token} />} />
        <Route path="/try" element={<TryPage token={token} />} />
        <Route path="/senders" element={<SendersPage token={token} />} />
        <Route path="/settings" element={<SettingsPage token={token} />} />
        <Route path="/team" element={<TeamPage token={token} />} />
        <Route path="/pricing" element={<PricingPage token={token} />} />
        <Route path="/join/:inviteToken" element={<JoinTeamPage token={token} />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage token={token} />} />
        <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}
